import React, { useRef, useState, useEffect } from "react";
import { Download, Upload, Share2, Copy, Send, Mail, Loader2, X } from "lucide-react";
import { toast } from "sonner";

// Safe Unicode Base64 Encoding
const encodeDraft = (data) => {
  try {
    const jsonStr = JSON.stringify(data);
    const bytes = new TextEncoder().encode(jsonStr);
    const binString = Array.from(bytes, (byte) => String.fromCodePoint(byte)).join("");
    return btoa(binString);
  } catch (e) {
    console.error(e);
    return "";
  }
};

// Safe Unicode Base64 Decoding
const decodeDraft = (base64Str) => {
  try {
    const binString = atob(base64Str);
    const bytes = Uint8Array.from(binString, (char) => char.codePointAt(0));
    const jsonStr = new TextDecoder().decode(bytes);
    return JSON.parse(jsonStr);
  } catch (e) {
    console.error(e);
    return null;
  }
};

export default function ShareDraftButtons({ 
  formData, 
  onImport, 
  brand = "cls", 
  formName = "draft",
  className = "" 
}) {
  const fileInputRef = useRef(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  // Check URL query parameters for draft data on load
  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const encodedData = urlParams.get("draft_data");
      
      if (encodedData) {
        const decoded = decodeDraft(encodedData);
        if (decoded) {
          // If the exported format had a wrapper payload, check inside it
          const actualData = decoded.form_data || decoded;
          onImport(actualData);
          toast.success("Loaded draft from shared link!");
          
          // Clean URL parameters without reloading
          const url = new URL(window.location.href);
          url.searchParams.delete("draft_data");
          window.history.replaceState({}, document.title, url.pathname + url.search);
        } else {
          toast.error("Could not parse shared draft link data.");
        }
      }
    } catch (err) {
      console.error("Failed to parse shared draft URL data:", err);
    }
  }, [onImport]);

  const handleExport = () => {
    try {
      if (!formData) {
        toast.error("No form data to export.");
        return;
      }
      
      const clientName = formData.client_name || 
                         (formData.first_name ? `${formData.first_name} ${formData.surname || formData.last_name || ""}`.trim() : "") || 
                         (formData.client_first_name ? `${formData.client_first_name} ${formData.client_surname || ""}`.trim() : "") || 
                         formData.employee_name || 
                         "Unnamed Client";

      const payload = {
        client_name: clientName,
        form_name: formName,
        brand,
        updated_at: new Date().toISOString(),
        form_data: formData
      };
      
      const dataStr = JSON.stringify(payload, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
      
      const clientNameSafe = clientName.trim().replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const exportFileDefaultName = `${brand}_${formName}_draft_${clientNameSafe}.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
      toast.success("Draft exported successfully!");
    } catch (e) {
      console.error(e);
      toast.error("Failed to export draft.");
    }
  };

  const handleImport = (e) => {
    const fileReader = new FileReader();
    const file = e.target.files[0];
    if (!file) return;

    fileReader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        if (!imported.form_data && !imported) {
          toast.error("Invalid draft file format. Could not load.");
          return;
        }
        onImport(imported.form_data || imported);
        toast.success(`Successfully imported draft!`);
        e.target.value = "";
      } catch (err) {
        console.error(err);
        toast.error("Failed to parse draft file. Make sure it is a valid JSON draft.");
      }
    };
    fileReader.readAsText(file);
  };

  const getShareUrl = () => {
    if (!formData) return "";
    const encoded = encodeDraft(formData);
    
    // Determine the base origin
    let baseOrigin = "";
    if (typeof window !== "undefined") {
      const hostname = window.location.hostname;
      if (
        hostname === "localhost" || 
        hostname === "127.0.0.1" || 
        hostname.includes("gitpod") || 
        hostname.includes("vercel.app")
      ) {
        baseOrigin = window.location.origin;
      } else {
        baseOrigin = "https://portal.completelawsupport.com";
      }
    }
    
    // Determine the correct pathname for this specific form and brand
    let pathname = "";
    if (typeof window !== "undefined") {
      let b = (brand || "").toLowerCase().trim();
      if (b === "vla" || b === "cls") {
        const currentUrl = window.location.href.toLowerCase();
        if (currentUrl.includes("vals")) b = "vals";
        else if (currentUrl.includes("lacw")) b = "lacw";
        else if (currentUrl.includes("jbl")) b = "jbl";
        else if (currentUrl.includes("eas")) b = "eas";
      }
      
      if (formName === "certification_sheet") {
        pathname = `/${b}-certification`;
      } else if (formName === "legal_aid") {
        pathname = `/${b}-aidapplication`;
      } else if (formName === "apply_for_aid") {
        pathname = `/${b}-applyforaid`;
      } else if (formName === "intake_form" || formName === "generic_intake") {
        pathname = `/${b}-intake`;
      } else if (formName === "authority_to_act") {
        pathname = `/${b}-authority`;
      } else if (formName === "backsheet_to_counsel") {
        pathname = `/${b}-backsheet`;
      } else if (formName === "vla_report_worksheet") {
        pathname = `/${b}-medicalworksheet`;
      } else if (formName === "memo_precedent") {
        pathname = `/${b}-memoprecedent`;
      } else if (formName === "travel_claims") {
        pathname = "/lacw-travelclaim";
      } else if (formName === "billing") {
        pathname = "/lacw-billing";
      } else {
        pathname = window.location.pathname;
      }
    }
    
    try {
      const url = new URL(pathname, baseOrigin);
      url.searchParams.set("draft_data", encoded);
      return url.toString();
    } catch (e) {
      console.error("Failed to construct share URL:", e);
      // Fallback to current location
      try {
        const url = new URL(window.location.href);
        url.searchParams.set("draft_data", encoded);
        return url.toString();
      } catch (err) {
        return "";
      }
    }
  };

  const handleCopyLink = () => {
    const url = getShareUrl();
    if (!url) return;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard!");
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!recipientEmail) {
      toast.error("Please enter a recipient email address.");
      return;
    }

    setSendingEmail(true);
    try {
      const url = getShareUrl();
      const clientName = formData.client_name || 
                         (formData.first_name ? `${formData.first_name} ${formData.surname || formData.last_name || ""}`.trim() : "") || 
                         (formData.client_first_name ? `${formData.client_first_name} ${formData.client_surname || ""}`.trim() : "") || 
                         formData.employee_name || 
                         "Client";
      const formLabel = formName.replace(/_/g, " ").toUpperCase();
      
      const emailSubject = `Shared Draft: ${formLabel} for ${clientName}`;
      const emailBody = `
Hi,

A draft for ${clientName} has been shared with you on Complete Law Support.

Click the link below to open the form with the draft data prefilled:
${url}

Regards,
Complete Law Support Portal
      `.trim();

      const response = await fetch("/api/send-draft-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: recipientEmail,
          subject: emailSubject,
          body: emailBody
        })
      });

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || "Failed to send email");
      }

      toast.success(`Draft link sent successfully to ${recipientEmail}!`);
      setShowShareModal(false);
      setRecipientEmail("");
    } catch (err) {
      console.error(err);
      toast.error("Failed to send draft link email.");
    } finally {
      setSendingEmail(false);
    }
  };

  const clientName = formData ? (formData.client_name || 
                     (formData.first_name ? `${formData.first_name} ${formData.surname || formData.last_name || ""}`.trim() : "") || 
                     (formData.client_first_name ? `${formData.client_first_name} ${formData.client_surname || ""}`.trim() : "") || 
                     formData.employee_name || 
                     "Client") : "Client";
  const shareUrl = getShareUrl();
  const mailtoUrl = `mailto:?subject=Shared Draft: ${formName.replace(/_/g, " ")} for ${encodeURIComponent(clientName)}&body=Click the link below to open the shared draft:%0D%0A%0D%0A${encodeURIComponent(shareUrl)}`;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImport}
        accept=".json"
        className="hidden"
      />
      <button
        type="button"
        onClick={handleExport}
        className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold uppercase tracking-wide text-xs hover:bg-slate-50 transition-all select-none flex items-center gap-1.5"
        title="Export draft to JSON file to share with other staff"
      >
        <Download className="w-3.5 h-3.5" /> Export File
      </button>
      <button
        type="button"
        onClick={() => fileInputRef.current.click()}
        className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold uppercase tracking-wide text-xs hover:bg-slate-50 transition-all select-none flex items-center gap-1.5"
        title="Import draft from a shared JSON file"
      >
        <Upload className="w-3.5 h-3.5" /> Import File
      </button>
      <button
        type="button"
        onClick={() => setShowShareModal(true)}
        className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold uppercase tracking-wide text-xs hover:bg-slate-50 transition-all select-none flex items-center gap-1.5"
        title="Get a shareable draft link or email it to other staff"
      >
        <Share2 className="w-3.5 h-3.5" /> Share Link
      </button>

      {/* Share Modal Dialog */}
      {showShareModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-lg w-full overflow-hidden p-6 relative">
            <button
              onClick={() => setShowShareModal(false)}
              className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-purple-600" />
                  Share Draft Link
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Anyone with this link will see your current form data prefilled immediately upon opening.
                </p>
              </div>

              {/* Copy Link Section */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Draft URL</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={shareUrl}
                    className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-mono text-slate-600 select-all"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Copy
                  </button>
                </div>
              </div>

              {/* Email Link Form */}
              <form onSubmit={handleSendEmail} className="border-t border-slate-100 pt-4 space-y-3">
                <label className="text-xs font-bold text-slate-700 block">Email Link Directly</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="email"
                      placeholder="colleague@completelawsupport.com"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      className="w-full border border-slate-200 rounded-xl pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sendingEmail}
                    className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-5 py-2.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors"
                  >
                    {sendingEmail ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Sending
                      </>
                    ) : (
                      <>
                        <Send className="w-3.5 h-3.5" />
                        Send Link
                      </>
                    )}
                  </button>
                </div>
              </form>

              {/* Fallback Email Client */}
              <div className="text-center pt-2">
                <a
                  href={mailtoUrl}
                  className="text-xs font-semibold text-purple-600 hover:text-purple-700 flex items-center justify-center gap-1.5"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Or send using your default mail app (Outlook, Mail, etc.)
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
